<script setup lang="ts">
import {ref} from 'vue';
import type {LegalDocItem} from '@/ts/types/dto/tos-dto';
import {useLoadingDataRefNull} from '@/ts/composables/pure/use-loading-data-ref';
import {useApi} from '@/ts/composables/stateful/use-api';
import ErrorBox from '@/vue/molecules/error-box.vue';
import HtmlFormatted from '@/vue/molecules/html-formatted.vue';
import SidebarName from '@/vue/molecules/sidebar/sidebar-name.vue';

defineProps<{
  imprint: LegalDocItem | null;
}>();

enum Tabs {
  IMPRINT = 'imprint',
  PRIVACY_POLICY = 'privacy_policy',
  TERMS_OF_USE = 'terms_of_use',
}

const api = useApi();

const tab = ref<Tabs>(Tabs.IMPRINT);
const [toc, loading, error] = useLoadingDataRefNull(async() => api.public.getLegalDocs());
</script>
<template>
  <div>
    <sidebar-name :name="$t('legal_documents')"/>

    <!-- Skeleton loading state -->
    <div v-if="loading" class="legaldocs-skeleton">
      <div class="skeleton-tabs">
        <q-skeleton type="QBtn" class="skeleton-tab"/>
        <q-skeleton type="QBtn" class="skeleton-tab"/>
        <q-skeleton type="QBtn" class="skeleton-tab"/>
      </div>
      <div class="skeleton-text-content">
        <q-skeleton type="text"/>
        <q-skeleton type="text"/>
        <q-skeleton type="text"/>
        <q-skeleton type="text" width="80%"/>
        <q-skeleton type="text" class="skeleton-paragraph-break"/>
        <q-skeleton type="text"/>
        <q-skeleton type="text"/>
        <q-skeleton type="text" width="60%"/>
      </div>
    </div>

    <!-- Actual content -->
    <template v-else-if="!error">
      <q-tabs v-model="tab" class="tabs" no-caps>
        <q-tab :label="$t('imprint')" :name="Tabs.IMPRINT"/>
        <q-tab :label="$t('privacy_policy')" :name="Tabs.PRIVACY_POLICY"/>
        <q-tab :label="$t('terms_of_use')" :name="Tabs.TERMS_OF_USE"/>
      </q-tabs>

      <q-tab-panels v-model="tab" animated class="sidebar-panel">
        <q-tab-panel :name="Tabs.IMPRINT">
          <div class="content-card">
            <html-formatted :html="(imprint ?? toc!.imprint).legalCopyHTML" background="neutral-0"/>
          </div>
        </q-tab-panel>
        <q-tab-panel :name="Tabs.PRIVACY_POLICY">
          <div class="content-card">
            <html-formatted :html="toc!.privacyPolicy.legalCopyHTML" background="neutral-0"/>
          </div>
        </q-tab-panel>
        <q-tab-panel :name="Tabs.TERMS_OF_USE">
          <div class="content-card">
            <html-formatted :html="toc!.termsAndConditions.legalCopyHTML" background="neutral-0"/>
          </div>
        </q-tab-panel>
      </q-tab-panels>
    </template>

    <!-- Error state -->
    <error-box v-else/>
  </div>
</template>
<style lang="scss" scoped>
.sidebar-panel {
  background-color: inherit;

  :deep(.q-tab-panel) {
    padding: var(--Spacing-spacing-4) 0;
  }
}

.tabs {
  @include tabs;
  padding-top: var(--Spacing-spacing-4);
}

.legaldocs-skeleton {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
}

.skeleton-tabs {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: var(--Spacing-spacing-2);
  margin-top: var(--Spacing-spacing-4);
}

.skeleton-tab {
  height: 36px;
  width: 100px;
  border-radius: 100px;
}

.skeleton-text-content {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-2);
}

.skeleton-paragraph-break {
  margin-top: var(--Spacing-spacing-3);
}

.content-card {
  border-radius: var(--card-border-radius, 12px);
  background: var(--Neutral-UI-neutral-ui-0, #F5F1EE);
  padding: var(--Spacing-spacing-6) var(--Spacing-spacing-8) var(--Spacing-spacing-9) var(--Spacing-spacing-6);
}

</style>

